import { type DeepMergeOptions, deepmerge, schedule } from '@sohanemon/utils';
import { ONE_DAY_IN_MS, ONE_MINUTE_IN_MS } from '@sohanscript/lib/constants';
import { dismissToast } from '@sohanscript/lib/utils/helper';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import SuperJSON from 'superjson';
import type { MutationContext, UpdateDefinition } from '@/types/global';
import { api } from '../trpc/client';
import qutil from './query-client-utils';
import { setupQueryDefaults } from './query-defaults-trpc';

export const createQueryClient = () => {
	const qc = new QueryClient({
		defaultOptions: {
			queries: {
				meta: { persist: true, dynamic: false },
				networkMode: 'offlineFirst',
				// enabled: ({ queryKey }) => !isSSR && qutil.getIsEnabled(queryKey),
				retry: 2,
				staleTime: ({ meta }) =>
					meta?.dynamic ? ONE_MINUTE_IN_MS : ONE_DAY_IN_MS,
				gcTime: Number.POSITIVE_INFINITY,
				refetchOnMount: false,
				refetchOnWindowFocus: ({ meta }) => !!meta?.dynamic,
				refetchOnReconnect: ({ meta }) => !!meta?.dynamic,
			},
			dehydrate: {
				serializeData: SuperJSON.serialize,
				shouldDehydrateQuery: (query) => {
					if (query.meta?.dynamic) return false;
					if (query.meta?.persist === false) return false;
					if (query.state.status !== 'success') return false;

					return true;
				},
				shouldDehydrateMutation: () => false,
			},
			hydrate: {
				deserializeData: SuperJSON.deserialize,
			},
		},

		queryCache: new QueryCache({
			onSuccess(data, query) {
				qutil.showAlert('success', {
					message: query.meta?.alerts?.success,
					context: { data },
				});
				query.meta?.onSuccess?.(data);
			},
			onError(error, query) {
				qutil.showAlert('error', {
					message:
						query.meta?.alerts?.error === true
							? error.message
							: query.meta?.alerts?.error,
					context: { error },
				});
				query.meta?.onError?.(error);
			},
			onSettled(data, error, query) {
				qutil.showAlert('info', {
					message: query.meta?.alerts?.settled,
					context: { data, error },
				});
				query.meta?.onSettle?.(data, error);
			},
		}),

		mutationCache: new MutationCache({
			onMutate(variables, mutation) {
				let context: MutationContext = {};

				const upds = mutation.options?.meta?.optimisticUpdate;
				if (upds) {
					context = qutil.applyOptimisticUpdates(upds, {
						context,
						variables,
					});
				}

				qutil.showAlert('loading', {
					message: mutation.meta?.alerts?.loading,
					context: { variables },
				});
				qutil.showAlert('success', {
					message: mutation.meta?.alerts?.triggered,
					context: { variables },
				});

				return context;
			},
			onSuccess(data, variables, _context, mutation) {
				const upds = mutation.options?.meta
					?.updateOnSuccess as UpdateDefinition<any, any>;
				if (upds) {
					qutil.applyOptimisticUpdates(upds, { data, variables });
				}

				qutil.showAlert('success', {
					message: mutation.meta?.alerts?.success,
					context: { data, variables },
				});

				qutil.runRouteOperations({
					routes: mutation.meta?.refetchRoutes,
					op: 'refetch',
					data,
					variables,
				});
				qutil.runRouteOperations({
					routes: mutation.meta?.removeRoutes,
					op: 'remove',
					data,
					variables,
				});
			},
			onError(error, variables, context, mutation) {
				console.info('⚡[query-client.ts:] error:', error);

				const upds = mutation.options?.meta?.optimisticUpdate;
				if (upds) {
					const list = Array.isArray(upds) ? upds : [upds];

					for (const upd of list) {
						// @ts-expect-error route must be typed
						const queryKey = getQueryKey(api[upd.route]);

						qc.setQueryData(
							queryKey,
							(context as MutationContext)?.prevData[upd.route],
						);
					}
				}

				if (error?.message !== 'COND_ERROR') {
					qutil.showAlert('error', {
						message:
							mutation.meta?.alerts?.error === true
								? error.message
								: mutation.meta?.alerts?.error,
						context: { error, variables },
					});
				}
			},
			onSettled(data, error, variables, _context, mutation) {
				const loadingMessage = mutation.meta?.alerts?.loading;
				const resolvedLoading =
					typeof loadingMessage === 'function'
						? loadingMessage({ type: 'loading', data, variables })
						: loadingMessage;

				let title: string | undefined;
				if (typeof resolvedLoading === 'string') {
					title = resolvedLoading;
				} else if (resolvedLoading && typeof resolvedLoading === 'object') {
					title = (resolvedLoading as { title: string }).title;
				}
				if (title) dismissToast(title as string);

				qutil.showAlert('info', {
					message: mutation.meta?.alerts?.settled,
					context: { data, error, variables },
				});

				// INFO: none TRPC
				const queryKey = mutation.meta?.invalidateQueries;
				if (queryKey) qc.invalidateQueries({ queryKey });

				qutil.runRouteOperations({
					routes: mutation.meta?.invalidateRoutes,
					op: 'invalidate',
					data,
					variables,
				});
			},
		}),
	});

	return qc;
};

let clientQueryClientSingleton: QueryClient;

export const getQueryClient = () => {
	if (typeof window === 'undefined') {
		// Server: always make a new query client
		const qc = createQueryClient();

		schedule(() => setupQueryDefaults(qc));
		return qc;
	}
	// Browser: use singleton pattern to keep the same query client
	if (!clientQueryClientSingleton) {
		clientQueryClientSingleton = createQueryClient();

		schedule(() => setupQueryDefaults(clientQueryClientSingleton));
	}

	return clientQueryClientSingleton;
};

const queryClient = getQueryClient();
// NOTE: custom event
queryClient.getQueryCache().subscribe((event) => {
	const query = event.query;

	// onSuccess — fires when a fetch completes
	if (event.type === 'updated' && event.action.type === 'success') {
		query.meta?.onData?.(event.action.data);
	}

	// onMount — only fire for the observer that just mounted
	if (event.type === 'observerAdded' && query.state.status === 'success') {
		event.observer.options.meta?.onData?.(query.state.data);
	}
});

const original = queryClient.defaultQueryOptions.bind(queryClient);

queryClient.defaultQueryOptions = (options) => {
	const resolved = original(options);
	const defaultMeta = queryClient.getDefaultOptions().queries?.meta ?? {};

	return {
		...resolved,
		meta: deepmerge(defaultMeta ?? {}, options?.meta ?? {}, {
			arrayMerge: 'merge',
			functionMerge: 'compose',
		} satisfies DeepMergeOptions),
	};
};

export { queryClient };
